/**
 * OtpFlow Component
 *
 * Two-step OTP authentication flow.
 * Step 1: Enter phone number → POST /api/auth/otp/send (BFF)
 * Step 2: Enter OTP code → POST /api/auth/otp/verify (BFF)
 *
 * Token NEVER touches client — BFF sets HTTP-only cookie directly.
 *
 * W3.2 (auth-debt epic): the flow is ONE discriminated union — the `code`
 * step STRUCTURALLY carries the phone the code was sent to, so "on the
 * code step with no phone number" (and four independently-drifting
 * `useState`s) cannot be represented. Transitions are total: every
 * `setFlow` names the full next state.
 */
import { Link } from '@/shared/i18n/routing';
import { useLocalizeApiError } from '@/shared/i18n/useLocalizeApiError';
import { useTranslations } from '@/shared/i18n/useTranslations';
import { bff } from '@/shared/lib/bff';
import { useAuthGate } from '@/shared/providers/AuthProvider';
import { OTP_CODE_LENGTH } from '@letsgethome/lib/constants';
import { bffError } from '@letsgethome/lib/server-errors';
import { routes } from '@letsgethome/routes';
import type { E164Phone, LocalizedText } from '@letsgethome/types/branded';
import { AppButton, inFlight } from '@letsgethome/ui/app-button';
import { PhoneField } from '@letsgethome/ui/forms';
import { Input } from '@letsgethome/ui/input';
import { UnstyledButton } from '@letsgethome/ui/unstyled-button';
import { useMutation } from '@tanstack/react-query';
import { ArrowLeft } from 'lucide-react';
import { useCallback, useState } from 'react';

/**
 * The flow as a discriminated union. `error` lives WITH its step (a
 * transition always clears it); the `code` step's `sentTo` is the E.164
 * the OTP actually went to — resend and verify read it from the state, so
 * they can never disagree with what the user was told.
 */
type OtpFlowState =
  | { step: 'phone'; e164: E164Phone | null; error: LocalizedText | null }
  | { step: 'code'; sentTo: E164Phone; code: string; error: LocalizedText | null };

const INITIAL_STATE: OtpFlowState = { step: 'phone', e164: null, error: null };

export function OtpFlow() {
  const tCommon = useTranslations('common');
  const tOtp = useTranslations('auth.otp');
  const localizeApiError = useLocalizeApiError();
  const { onAuthSuccess } = useAuthGate();

  const [flow, setFlow] = useState<OtpFlowState>(INITIAL_STATE);

  const sendOtpMutation = useMutation({
    mutationFn: async (phone: E164Phone) => {
      const result = await bff('/api/auth/otp/send', { phoneNumber: phone });
      if (!result.success) {
        throw bffError(result.error);
      }
      return phone;
    },
    onSuccess: (phone) => setFlow({ step: 'code', sentTo: phone, code: '', error: null }),
    onError: (err) =>
      setFlow((prev) => ({ ...prev, error: localizeApiError(err, 'auth.otp.errorSend') })),
  });

  const verifyOtpMutation = useMutation({
    mutationFn: async ({ phone, otp }: { phone: E164Phone; otp: string }) => {
      const result = await bff('/api/auth/otp/verify', {
        phoneNumber: phone,
        code: otp,
      });
      if (!result.success) {
        throw bffError(result.error);
      }
      return result.data;
    },
    onSuccess: (user) => onAuthSuccess(user),
    onError: (err) =>
      setFlow((prev) => ({ ...prev, error: localizeApiError(err, 'auth.otp.errorVerify') })),
  });

  const isPending = sendOtpMutation.isPending || verifyOtpMutation.isPending;

  const handlePhoneChange = useCallback((e164: E164Phone | null) => {
    setFlow({ step: 'phone', e164, error: null });
  }, []);

  if (flow.step === 'phone') {
    const validPhone = flow.e164;
    const sendCode = () => {
      if (validPhone) sendOtpMutation.mutate(validPhone);
    };
    return (
      <div className="space-y-4">
        <PhoneField
          label={tOtp('phoneLabel')}
          value={flow.e164 ?? ''}
          onChange={handlePhoneChange}
          placeholder={tOtp('phonePlaceholder')}
          disabled={isPending}
          error={flow.error || undefined}
          size="md"
          selectCountryLabel={tCommon('a11y.selectCountry')}
        />

        <AppButton
          onClick={sendCode}
          disabled={!validPhone}
          loading={inFlight(isPending)}
          fullWidth
        >
          {tOtp('sendCode')}
        </AppButton>

        <p className="text-center text-xs text-muted-foreground">
          {tOtp('termsText')}{' '}
          <Link href={routes.terms()} className="underline hover:text-foreground">
            {tOtp('termsLink')}
          </Link>{' '}
          {tOtp('termsAnd')}{' '}
          <Link href={routes.privacy()} className="underline hover:text-foreground">
            {tOtp('privacyLink')}
          </Link>
        </p>
      </div>
    );
  }

  const resend = () => sendOtpMutation.mutate(flow.sentTo);
  const verify = () => verifyOtpMutation.mutate({ phone: flow.sentTo, otp: flow.code });
  const back = () => setFlow({ step: 'phone', e164: flow.sentTo, error: null });

  return (
    <div className="space-y-4">
      <UnstyledButton
        type="button"
        onClick={back}
        className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground"
        disabled={isPending}
      >
        <ArrowLeft className="mr-1 h-4 w-4" />
        {tOtp('back')}
      </UnstyledButton>
      <p className="text-sm text-muted-foreground">{tOtp('codeSentTo', { phone: flow.sentTo })}</p>
      <div className="space-y-2">
        <label htmlFor="code" className="text-sm font-medium text-foreground">
          {tOtp('codeLabel')}
        </label>
        <Input
          id="code"
          type="text"
          inputMode="numeric"
          placeholder={tOtp('codePlaceholder')}
          value={flow.code}
          onChange={(e) =>
            setFlow({
              ...flow,
              code: e.target.value.replace(/\D/g, '').slice(0, OTP_CODE_LENGTH),
              error: null,
            })
          }
          className="h-12 text-center text-lg tracking-widest"
          autoComplete="one-time-code"
          disabled={isPending}
          autoFocus
        />
      </div>
      {flow.error && (
        <p className="text-sm text-destructive" role="alert">
          {flow.error}
        </p>
      )}
      <AppButton
        onClick={verify}
        disabled={flow.code.length !== OTP_CODE_LENGTH}
        loading={inFlight(isPending)}
        fullWidth
      >
        {tOtp('verify')}
      </AppButton>
      <UnstyledButton
        type="button"
        onClick={resend}
        disabled={isPending}
        className="w-full text-center text-sm text-muted-foreground hover:text-foreground"
      >
        {tOtp('resend')}
      </UnstyledButton>
    </div>
  );
}
